import React from "react";
import { Block } from "../blockModel/types";
import { shade, ctext } from "./colorUtils";
import { RenderCtx, es, editableProps, contentProps } from "./renderText";
import { renderSummary } from "./blocks/renderSummary";
import { renderNotes } from "./blocks/renderNotes";

// Re-export public API that other modules import directly from this file.
export type { RenderCtx };
export { renderTextWithVars } from "./renderText";

/**
 * The exact logo the sent email uses — backend `public/images/whalelo-full-logo.png`,
 * embedded in mail via CID (`cid:whalelo-email-logo`). We can't reach the backend copy
 * cross-origin (CORP same-origin → ERR_BLOCKED_BY_RESPONSE.NotSameOrigin), so the same
 * file is vendored into the admin's own static dir and served same-origin.
 */
const WL_EMAIL_LOGO_URL = "/static/img/whalelo-full-logo.png";

// ---------- PER-BLOCK RENDERERS ----------

function renderHeader(block: Block, ctx: RenderCtx): JSX.Element {
	const { brand } = ctx.theme;
	const darkBrand = shade(brand, 0.38);
	const fg = ctext(brand);
	const id = block.id ?? "";
	const eyebrowKey = id + ":eyebrow";
	const titleKey = id + ":title";
	const subKey = id + ":subtitle";
	const editable = !!ctx.editable;

	// In edit mode, subtitle is always rendered so the user can type into it or
	// drop a variable token into it — even when the stored value is null/undefined.
	// In preview/read-only mode, omit it when empty (no visual noise).
	const showSubtitle = editable ? true : block.data.subtitle != null && block.data.subtitle !== "";
	// Eyebrow follows the same rule: always editable in edit mode, hidden when empty in preview.
	const showEyebrow = editable ? true : !!block.data.eyebrow;

	return (
		<div
			className="e-header"
			style={{
				background: `linear-gradient(135deg,${brand} 0%,${darkBrand} 100%)`,
				color: fg,
			}}>
			<div className="e-logo">
				<img
					src={WL_EMAIL_LOGO_URL}
					alt="WhaleLO"
				/>
			</div>
			{/* Eyebrow — rendered/editable in edit mode; serialised to "Eyebrow: …" */}
			{showEyebrow && (
				<p
					className="e-eyebrow"
					style={{
						margin: "0 0 4px",
						fontSize: 12,
						fontWeight: 600,
						opacity: 0.75,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						...es(ctx.elStyle, eyebrowKey),
					}}
					{...(editable
						? editableProps(eyebrowKey, "eyebrow", {
								"data-placeholder": "Eyebrow (tuỳ chọn)",
						  } as React.HTMLAttributes<HTMLElement>)
						: {})}
					{...contentProps(block.data.eyebrow ?? "", ctx)}
				/>
			)}
			<h1
				style={es(ctx.elStyle, titleKey)}
				{...(editable ? editableProps(titleKey, "title") : {})}
				{...contentProps(block.data.title ?? "", ctx)}
			/>
			{/* Subtitle — always present in edit mode so users can add content or drop vars */}
			{showSubtitle && (
				<p
					style={es(ctx.elStyle, subKey)}
					{...(editable
						? editableProps(subKey, "subtitle", {
								"data-placeholder": "Phụ đề (tuỳ chọn)",
						  } as React.HTMLAttributes<HTMLElement>)
						: {})}
					{...contentProps(block.data.subtitle ?? "", ctx)}
				/>
			)}
			{/* Header freeform lines — rendered below subtitle in the banner */}
			{(block.data.lines || []).map((line, i) => {
				if (!editable && !line) return null;
				const hdrLineKey = id + ":line:" + i;
				return (
					<div
						key={i}
						data-row={editable ? id + ":" + i : undefined}
						style={{
							position: "relative",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<p
							className="e-hdrline"
							style={{
								margin: "2px 0",
								fontSize: 13,
								opacity: 0.9,
								textAlign: "center",
								...es(ctx.elStyle, hdrLineKey),
							}}
							{...(editable
								? editableProps(hdrLineKey, "hdrline:" + i, {
										"data-placeholder": "Dòng nội dung…",
								  } as React.HTMLAttributes<HTMLElement>)
								: {})}
							{...contentProps(line, ctx)}
						/>
						{editable && (
							<button
								className="e-hdrline-del"
								title="Xoá dòng"
								contentEditable={false}
								data-hdrline-del={i}
								data-block-id={id}
								style={{
									position: "absolute",
									right: -20,
									top: "50%",
									transform: "translateY(-50%)",
									background: "rgba(255,255,255,0.2)",
									border: "none",
									borderRadius: "50%",
									width: 16,
									height: 16,
									cursor: "pointer",
									fontSize: 10,
									color: "#fff",
									lineHeight: "16px",
									padding: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}>
								×
							</button>
						)}
					</div>
				);
			})}
			{/* Add line affordance — only in edit mode */}
			{editable && (
				<button
					className="e-hdrline-add"
					contentEditable={false}
					data-hdrline-add="1"
					data-block-id={id}
					style={{
						marginTop: 6,
						background: "rgba(255,255,255,0.15)",
						border: "1px dashed rgba(255,255,255,0.5)",
						borderRadius: 4,
						color: "rgba(255,255,255,0.85)",
						fontSize: 11,
						padding: "2px 10px",
						cursor: "pointer",
					}}>
					+ Add line
				</button>
			)}
		</div>
	);
}

function renderChip(block: Block, ctx: RenderCtx): JSX.Element {
	const id = block.id ?? "";
	const textKey = id + ":text";
	const editable = !!ctx.editable;
	return (
		<div
			className="e-body"
			style={{ paddingBottom: 0 }}>
			<div className="e-chip">
				<span
					style={es(ctx.elStyle, textKey)}
					{...(editable ? editableProps(textKey, "text") : {})}
					{...contentProps(block.data.text ?? "", ctx)}
				/>
			</div>
		</div>
	);
}

function renderParagraph(block: Block, ctx: RenderCtx): JSX.Element {
	const id = block.id ?? "";
	const htmlKey = id + ":html";
	const editable = !!ctx.editable;
	return (
		<div
			className="e-body"
			style={{ paddingTop: 4, paddingBottom: 4 }}>
			<p
				className="e-p"
				style={es(ctx.elStyle, htmlKey)}
				{...(editable ? editableProps(htmlKey, "html") : {})}
				{...contentProps(block.data.html ?? block.data.text ?? "", ctx)}
			/>
		</div>
	);
}

function renderButton(block: Block, ctx: RenderCtx): JSX.Element {
	const id = block.id ?? "";
	const { brand } = ctx.theme;
	const labelKey = id + ":label";
	const overrideStyle = es(ctx.elStyle, labelKey);
	const fg = ctext(overrideStyle.background ? String(overrideStyle.background) : brand);
	const editable = !!ctx.editable;
	return (
		<div
			className="e-body"
			style={{ textAlign: "center", paddingTop: 8, paddingBottom: 8 }}>
			<span
				style={{
					background: brand,
					color: fg,
					fontWeight: 700,
					fontSize: 13,
					padding: "11px 24px",
					borderRadius: 8,
					display: "inline-block",
					...overrideStyle,
				}}
				{...(editable ? editableProps(labelKey, "label") : { "data-el": labelKey })}
				{...contentProps(block.data.label ?? block.data.text ?? "View Full Statement", ctx)}
			/>
		</div>
	);
}

function renderDivider(): JSX.Element {
	return (
		<div
			className="e-body"
			style={{ padding: "6px 24px" }}>
			<hr style={{ border: "none", borderTop: "1px solid #e3e8ef" }} />
		</div>
	);
}

function renderFooter(block: Block, ctx: RenderCtx): JSX.Element {
	// Footer content lives entirely in data.lines[] (serialize writes/reads only lines).
	// title / note were editable dead-ends — they would write state that serialize ignores.
	// Rendering is purely from lines so every edit round-trips correctly.
	const id = block.id ?? "";
	const lines = block.data.lines ?? [];
	const editable = !!ctx.editable;

	return (
		<div className="e-footer">
			{lines.map((line, i) => {
				const flineKey = id + ":fline:" + i;
				return (
					<div
						key={i}
						className="l"
						data-row={id + ":" + i}>
						<span
							style={es(ctx.elStyle, flineKey)}
							{...(editable ? editableProps(flineKey, "fline:" + i) : {})}
							{...contentProps(line, ctx)}
						/>
					</div>
				);
			})}
		</div>
	);
}

// ---------- MAIN DISPATCH ----------

/** Renders a single block to JSX. Read-only (no contentEditable). */
export function renderBlock(block: Block, ctx: RenderCtx): JSX.Element {
	switch (block.type) {
		case "header":
			return renderHeader(block, ctx);
		case "chip":
			return renderChip(block, ctx);
		case "paragraph":
			return renderParagraph(block, ctx);
		case "summary":
			return renderSummary(block, ctx);
		case "notes":
			return renderNotes(block, ctx);
		case "button":
			return renderButton(block, ctx);
		case "divider":
			return renderDivider();
		case "footer":
			return renderFooter(block, ctx);
		default:
			return <div />;
	}
}
